<script setup>
import {reactive, onMounted, onUnmounted, ref, computed} from "vue";

  import {ApiWrapper} from "@/Utils/ApiWrapper";
  import {activeComponentStore} from "@/Stores/ActiveComponentStore";
  import {Permissions} from "@/Utils/Permissions";
  import {ToastUtil} from "@/Utils/ToastUtil";

  const compName = 'AdminsComponent';

  const props = defineProps({
    contextSchemaA: String,
    contextIdA: String
  });

  const form = ref(null);

  const data = reactive({
    hoveredHomephone: false,
    hoveredMobilephone: false,
    contextIdA: null,
    hovered2: false,
    isDisabled: false,
    isDisabled2: false,
    isDisabled3: false,
    isDisabled4: false,
    isDisabled5: false,
    valid: true,
    loading: true,
    dialog: false,
    addingPerson: false,
    newName: null,
    newRole: null,
    items: [],
    names: [],
    roles: [],
    selected: null,
    contactSelected: false,
    phoneMessage: false,
    phoneMessageDetails: '',
    emailMessage: true,
    emailMessageDetails: '',
  });

  const saveMessage = (type, personid) => {
    data.loading = true
    if (type === 'phone') {
      ApiWrapper.post('messaging', 'create-message', {
            claimId: props.contextIdA,
            message: data.phoneMessageDetails,
            personid: personid,
          })
          .then((response) => {
            activeComponentStore.notifyComponents(['messages'],compName);
            data.phoneMessage = false;
            data.phoneMessageDetails = '';
            getData();

            ToastUtil.successMsg('Created message');
          })
          .catch((e) => {
            ToastUtil.error(e);

            data.loading = false;
          })
    } else if (type === 'email') {
      ApiWrapper.post('messaging', 'create-message', {
            claimId: props.contextIdA,
            message: data.emailMessageDetails,
            personid: personid,
          })
          .then((response) => {
            activeComponentStore.notifyComponents(['messages'],compName);
            data.emailMessage = false
            data.emailMessageDetails = ''
            getData();

            ToastUtil.successMsg('Created message');
          })
          .catch((e) => {
            ToastUtil.error(e);

            data.loading = false;
          })
    }
  }
  
  const removePerson = (pid, rid) => {
    data.loading = true
    ApiWrapper.post('admins', 'remove-person', {
          pid: pid,
          rid: rid,
          claimId: props.contextIdA,
        })
        .then((response) => {
          activeComponentStore.notifyComponents(['adjusters','person'],compName);
          getData();

          ToastUtil.successMsg('Removed contact');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        });
  }
  
  const selectContact = (item) => {
    data.selected = JSON.parse(JSON.stringify(item))
    data.editedItem = JSON.parse(JSON.stringify(item))

    data.emailMessage = data.selected.email !== null;
  }
  
  const formatPhoneNumber = (phoneNumberString) => {
    let cleaned = ('' + phoneNumberString).replace(/\D/g, '')
    let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3]
    }
    return null
  }
  
  const getData = () => {
    data.addingPerson = false
    data.isDisabled = false
    data.isDisabled2 = false
    data.isDisabled3 = false
    data.isDisabled4 = false
    data.isDisabled5 = false
    data.loading = true

    ApiWrapper.post('admins', 'admins', {
        claimId: props.contextIdA,
      })
      .then((response) => {
        data.items = []
        for (let x in response.admins) {
          data.items.push(response.admins[x])
        }

        data.names = response.names.map(function (obj) {
          obj.title = obj.text;
          return obj;
        });

        data.roles = response.roles.map(function (obj) {
          return obj.role
        })

        data.newName = null
        data.newRole = null
        data.dialog = false
        data.contactSelected = false
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.loading = false
      });
  }
  
  const newPerson = () => {
    if (form.value.validate()) {
      data.loading = true
      data.isDisabled = true
      ApiWrapper.post('admins', 'new-person', {
            newName: data.newName,
            newRole: data.newRole,
            claimId: props.contextIdA,
          })
          .then((response) => {
            getData();
            activeComponentStore.notifyComponents(['person','adjusters','messages'],compName);

            ToastUtil.successMsg('Added new person');

            if(response.thirdPartySuccess) {
              ToastUtil.successMsg(`Assigned person in ${response.thirdPartyName}`);
            }
            /** Errors **/
            else if(response.thirdPartyAddAdjusterError) {
              ToastUtil.error(response.thirdPartyAddAdjusterError);
            }
            else if(response.xactAddAdjusterError) {
              ToastUtil.error(response.xactAddAdjusterError);
            }
          })
          .catch((err) => {
            ToastUtil.error(err);
            data.loading = false;
            data.isDisabled = false;
          })
    }
  }
  
  const cancel = () => {
    data.addingPerson = false
    data.newName = null
    data.newRole = null
  }

  const rolesList = computed(() => {
    // If there is already an adjuster on this claim, then remove that role from the role list
    if(data.items?.filter((person) => person.role === 'Adjuster').length > 0) {
      return data.roles.filter((role) => role !== 'Adjuster');
    }
    // Otherwise, show entire role list
    else {
      return data.roles;
    }
  })

  onMounted(()=>{
    activeComponentStore.registerComponent(
        compName,
        [
          'clients'
        ],
        function() {
          data.loading = true
          setTimeout(function(){ getData() }, 5000) // wait a little bit to allow DB event handler to update admins
        }
    );
    data.contextIdA = props.contextIdA
    getData();
  });

  onUnmounted(() => {
    activeComponentStore.deregisterComponent(compName);
  });
</script>

<template>
  <v-container fluid>
    <v-card class="elevation-2 px-2" height="auto" width="auto">
      <v-toolbar density="compact" flat color="white">
        <v-toolbar-title>Admins</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        >
        </v-progress-linear>

        <v-divider class="mx-4" inset vertical></v-divider>

        <RouterLink :to="`/bulkassignspecificclaim/${contextIdA}`" v-if="Permissions.checkPermissions(['claims.bulk-assign'])">
          Map Assign
        </RouterLink>
        <v-spacer></v-spacer>


        <v-btn
            size="small"
            variant="elevated"
            v-if="!data.addingPerson && Permissions.checkPermissions(['claims.add-person'])"
            color="primary"
            @click="data.addingPerson = true"
        >New Person</v-btn
        >
      </v-toolbar>

      <template v-if="data.addingPerson">
        <v-form
            ref="form"
            v-model="data.valid"
            lazy-validation
        >
          <v-autocomplete
              class="px-3"
              v-model="data.newName"
              :items="data.names"
              :rules="[v => !!v || 'Person is required']"
              label="Type or Select Person's Name"
              clearable
          ></v-autocomplete>
          <v-autocomplete
              class="px-3"
              v-model="data.newRole"
              :items="rolesList"
              :rules="[v => !!v || 'Role is required']"
              label="Type or Select Person's Role"
              clearable
          ></v-autocomplete>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
                size="small"
                variant="elevated"
                color="error"
                class="white--text mr-2"
                @click="cancel"
            >Cancel</v-btn
            >
            <v-btn
                size="small"
                variant="elevated"
                color="primary"
                class="white--text"
                :disabled="!data.valid || data.isDisabled === true"
                @click="newPerson"
            >Submit</v-btn
            >
          </v-card-actions>
        </v-form>
      </template>

      <template v-else>
        <template v-if="!data.contactSelected">
          <v-list>
            <v-list-item
                id="content"
                density="compact"
                two-line
                v-for="item in data.items"
                :key="item.name"
                @click="selectContact(item); data.contactSelected=true"
            >
                <v-list-item-subtitle>{{item.role}}</v-list-item-subtitle>
                <v-list-item-title>{{item.name}}</v-list-item-title>
            </v-list-item>
          </v-list>
        </template>
        <template v-else>
            <v-list>
              <v-card-subtitle class="py-0 font-weight-bold">{{data.selected.role}}</v-card-subtitle>
              <div class="pl-4 py-1">
                <v-list-item-subtitle>Name</v-list-item-subtitle>
                <v-list-item-title>{{data.selected.name}}</v-list-item-title>
                <RouterLink :to="`/userprofile/${data.selected.personid}`" v-if="Permissions.checkPermissions(['users.read'])">
                  View Profile
                </RouterLink>
              </div>
              <div
                  v-if="data.selected.homephone !== null"
                  :class="{'homephone-hover': data.hoveredHomephone}"
                  class="pl-4 py-1"
                  style="cursor:pointer"
                  @mouseover="data.hoveredHomephone = true"
                  @mouseleave="data.hoveredHomephone = false"
              >
                <v-list-item-subtitle>Home Phone</v-list-item-subtitle>
                <v-list-item-title>{{formatPhoneNumber(data.selected.homephone)}}</v-list-item-title>
              </div>
              <div
                  v-if="data.selected.mobilephone !== null"
                  :class="{'mobilephone-hover': data.hoveredMobilephone}"
                  class="pl-4 py-1"
                  style="cursor:pointer"
                  @mouseover="data.hoveredMobilephone = true"
                  @mouseleave="data.hoveredMobilephone = false"
              >
                <v-list-item-subtitle>Mobile Phone</v-list-item-subtitle>
                <v-list-item-title>{{formatPhoneNumber(data.selected.mobilephone)}}</v-list-item-title>
              </div>

              <div style="margin: 10px" v-if="data.phoneMessage === true && Permissions.checkPermissions(['messages.create'])">
                <v-textarea
                    filled
                    v-model="data.phoneMessageDetails"
                    auto-grow
                    rows="2"
                    label="Phone Message Details"
                >
                </v-textarea>
                <v-card-actions>
                  <v-btn
                      size="small"
                      variant="elevated"
                      color="error"
                      @click="data.phoneMessage = false; data.phoneMessageDetails = ''"
                  >Cancel</v-btn>
                  <v-btn
                      @click="saveMessage('phone', data.selected.personid); data.isDisabled4 = true"
                      color="primary"
                      v-if="data.phoneMessageDetails !== ''"
                      :disabled="data.isDisabled4 === true"
                      size="small"
                      variant="elevated"
                  >Submit</v-btn>
                </v-card-actions>
                <v-divider></v-divider>
              </div>
              <div
                  v-if="data.selected.email !== null"
                  :class="{'email-hover': data.hovered2}"
                  class="pl-4 py-1"
                  style="cursor:pointer"
                  @mouseover="data.hovered2 = true"
                  @mouseleave="data.hovered2 = false">
                <v-list-item-subtitle>Email</v-list-item-subtitle>
                <v-list-item-title>{{data.selected.email}}</v-list-item-title>
              </div>
              <div style="margin: 10px" v-if="data.emailMessage === true && Permissions.checkPermissions(['messages.create'])">
                <v-textarea
                    filled
                    v-model="data.emailMessageDetails"
                    auto-grow
                    rows="2"
                    label="Email Message Details"
                >
                </v-textarea>
                <v-card-actions>
                  <v-btn
                      size="small"
                      variant="elevated"
                      color="error"
                      @click="data.emailMessage = false; data.emailMessageDetails = ''"
                  >Cancel</v-btn>
                  <v-btn
                      @click="saveMessage('email', data.selected.personid); data.isDisabled5 = true"
                      color="primary"
                      v-if="data.emailMessageDetails !== ''"
                      :disabled="data.isDisabled5 === true"
                      size="small"
                      variant="elevated"
                  >Submit</v-btn>
                </v-card-actions>
                <v-divider></v-divider>
              </div>

              <!-- <div>
                <v-subheader style="height: 20px" v-if="selected.messages.length > 0" inset>Recent Messages</v-subheader>
                <v-subheader style="height: 20px" v-else inset>No Messages</v-subheader>
              </div>

              <div v-if="selected.messages.length > 0">
                <v-list-item
                  density="compact"
                  v-for="(message,index) in selected.messages"
                  v-if="index <= 1"
                >
                  <v-list-item-content class="py-0">
                    <v-list-item-subtitle class="mb-0">
                      {{selected.created_by[index]}} -
                      {{selected.created_at[index]}}
                    </v-list-item-subtitle>
                    <v-list-item-title>{{selected.messages[index]}}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </div> -->

            </v-list>
            <v-card-actions>
              <v-btn size="small" variant="text" class="primary--text" @click="data.contactSelected = false">
                <v-icon>mdi-arrow-left</v-icon>
                Back
              </v-btn>
              <v-dialog
                  v-model="data.dialog"
                  persistent
                  max-width="290">
                <template v-slot:activator="{ on, props }">
                  <v-btn
                      size="small"
                      variant="elevated"
                      v-if="Permissions.checkPermissions(['claims.remove-person'])"
                      color="error"
                      class="ml-2"
                      dark
                      v-bind="props"
                      v-on="on"
                  >
                    Remove
                    <br>
                    Contact
                  </v-btn>
                </template>
                <v-card>
                  <v-card-text class="pt-2">Are you sure you would like to remove the selected contact?</v-card-text>
                  <v-card-actions>
                    <v-btn
                        size="small"
                        color="error"
                        variant="text"
                        @click="data.dialog = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                        size="small"
                        color="green darken-1"
                        variant="text"
                        @click="removePerson(data.selected.personid, data.selected.roleid)"
                    >
                      Confirm
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-card-actions>

        </template>

      </template>
    </v-card>
  </v-container>
</template>

<style scoped>
  .container {
    padding: 5px;
  }

  [v-cloak] {
    display: none;
  }

  .v-list-item-subtitle {
    font-size: 10px !important;
    color:black !important;
  }
  .v-list-item-title {
    font-size: 13px !important;
    color: black !important;
  }
  .v-card-subtitle {
    color:black !important;
  }
  #profile {
    font-size: 13px !important;
  }
  .v-list-item {
    min-height: 40px !important;
  }
  #content {
    padding: 0 !important
  }
  .mobilephone-hover {
    background-color: #E3F2FD;
  }
  .homephone-hover {
    background-color: #E3F2FD;
  }
  .email-hover {
    background-color: #E3F2FD;
  }
</style>